import { CommonModule } from '@angular/common'
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core'
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms'
import * as fastLevenshtein from 'fast-levenshtein'

import { CaseInput } from '../../../interfaces/case-input.interface'
import { HTMLInputEvent } from '../../../interfaces/html-input-event.interface'
import { ResourceDefinition } from '../../../interfaces/resource-definition.interface'
import { SearchResult } from '../../../interfaces/search-result.interface'
import { SelectOption } from '../../../interfaces/select-option.interface'
import { ResourceService } from '../../../services/resource.service'
import { isReadOnlyTaxonomy } from '../../../utils/taxonomy-access.functions'

@Component({
    selector: 'case-multi-search-input',
    templateUrl: './multi-search-input.component.html',
    styleUrls: ['./multi-search-input.component.scss'],
    providers: [ResourceService],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule]
})
export class MultiSearchInputComponent implements CaseInput, OnChanges, OnDestroy {
  @Input() label: string
  @Input() initialValue: {
    [key: string]: string[] | number
  }
  @Input() placeholder: string
  @Input() helpText: string
  @Input() htmlHelpText?: string
  @Input() resources: ResourceDefinition[]
  @Input() filter: ResourceDefinition
  @Input() groupBy?: string
  @Input() params: { [key: string]: string | string[] } = {}
  @Input() maxSelectedItems: number
  @Input() readonly = false
  @Input() showErrors = false
  @Input() validators: ValidatorFn[] = []
  @Input() uniqueId: string
  @Input() showCreateButton = false
  @Input() maxResults: number = 0

  /**
   * Returns an object with selected results by resource. In case of a single selection, we return the id of the selected item.
   */
  @Output() valueChanged: EventEmitter<
    | {
        [key: string]: number[]
      }
    | number
  > = new EventEmitter()

  @ViewChild('searchInput', { static: false }) searchInputEl: ElementRef

  suggestedSearchResults: SearchResult[] = []
  selectedSearchResults: SearchResult[] = []
  filterItems: SelectOption[] = []
  activeFilterItem: SelectOption

  private searchTimeout: any
  terms = ''
  showList = false
  showCreateModal = false
  focusedItemIndex: number
  required: boolean
  defaultIcon = 'icon-grid'
  isReadOnlyTaxonomy = isReadOnlyTaxonomy
  createForm: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl('', Validators.required)
  })

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private resourceService: ResourceService
  ) {}

  ngOnDestroy() {
    clearTimeout(this.searchTimeout)
  }

  async ngOnChanges() {
    this.selectedSearchResults = await this.getSearchResultObjects(
      this.initialValue
    ).then((res) => res)

    if (this.filter) {
      this.filterItems = await this.resourceService
        .listSelectOptions(this.filter.slug)
        .then((res) => res)
    }

    this.required = this.validators.includes(Validators.required)
  }

  // Fetch full objects from API to display them. Based on initialValue (ids ony).
  getSearchResultObjects(initialValue: any): Promise<SearchResult[]> {
    // If we just have one resource, we get the resourceName from the resource prop to build
    // an object for the query.
    if (this.resources.length === 1) {
      if (this.maxSelectedItems === 1 && typeof initialValue === 'number') {
        // Convert single number to array for search result objects
        initialValue = {
          [this.firstLetterInLowerCase(this.resources[0].className) + 'Ids']: [
            initialValue
          ]
        }
      } else {
        initialValue = {
          [this.firstLetterInLowerCase(this.resources[0].className) + 'Ids']:
            initialValue
        }
      }
    }

    if (initialValue && Object.values(initialValue).some((v) => !!v)) {
      return this.resourceService.list(
        'search/get-search-result-objects',
        initialValue
      )
    }
    return Promise.resolve([])
  }

  toggleItem(item: SearchResult): void {
    if (this.readonly || item.resourceName === 'groupHeader') {
      return
    }
    // Check if there already is an item with same id on selection
    if (this.selectedSearchResults.find((i: any) => i.id === item.id)) {
      this.selectedSearchResults.splice(
        this.selectedSearchResults.findIndex((i: any) => i.id === item.id),
        1
      )
      this.renderer.setProperty(this.searchInputEl.nativeElement, 'value', '')
    } else if (
      !this.maxSelectedItems ||
      this.selectedSearchResults.length < this.maxSelectedItems
    ) {
      this.selectedSearchResults.push(item)
      this.renderer.setProperty(this.searchInputEl.nativeElement, 'value', '')
    }
    this.showList = false
    this.suggestedSearchResults = []

    this.valueChanged.emit(this.formatToEmit(this.selectedSearchResults))
  }

  /**
   * Toggle filter item selection.
   *
   * @param item - The filter item to toggle.
   * @returns void
   */
  toggleFilterItem(item: SelectOption): void {
    this.filterItems = this.filterItems.map((i) => {
      i.selected = i === item ? !i.selected : false
      return i
    })

    this.activeFilterItem = this.filterItems.find((i) => i.selected)

    const paramKey: string =
      this.filter.className.charAt(0).toLowerCase() +
      this.filter.className.slice(1) +
      'Ids'

    if (!this.params) {
      this.params = {}
    }

    this.params[paramKey] = this.filterItems
      .filter((f) => f.selected)
      .map((f) => f.value.toString())

    // Update search results.
    this.onSearchInputKeyup()
  }

  isAllSelected(): boolean {
    return this.suggestedSearchResults.every((result: SearchResult) =>
      this.selectedSearchResults.find((i: any) => i.id === result.id)
    )
  }

  isSelected(item: SearchResult): boolean {
    return !!this.selectedSearchResults.find((i: any) => i.id === item.id)
  }

  selectAll(): void {
    this.suggestedSearchResults.forEach((result: SearchResult) => {
      if (!this.selectedSearchResults.find((i: any) => i.id === result.id)) {
        this.selectedSearchResults.push(result)
      }
    })
  }

  unselectAll(): void {
    this.suggestedSearchResults.forEach((result: SearchResult) => {
      this.selectedSearchResults = this.selectedSearchResults.filter(
        (i: any) => i.id !== result.id
      ) as SearchResult[]
    })
  }

  onSearchInputKeyup(event?: HTMLInputEvent): void {
    if (this.readonly) {
      return
    }

    if (event) {
      this.terms = event.target.value

      // Navigate through results
      if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
        return this.navigateSuggestedValues(event.key)
      }
    }

    this.showList = true

    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
    this.resourceService
      .list('search', {
        resources: this.resources.map((rD) => rD.className),
        terms: this.terms,
        ...this.params
      })
      .then((searchResultsRes: SearchResult[]) => {
        // Sort by Levenshtein distance
        this.suggestedSearchResults = searchResultsRes.sort(
          (a: SearchResult, b: SearchResult) =>
            fastLevenshtein.get(this.terms, a.label) -
            fastLevenshtein.get(this.terms, b.label)
        )

        if (this.groupBy) {
          // Get unique group names.
          const groupItemNames = this.suggestedSearchResults
            .map((result: SearchResult) => result[this.groupBy]?.name)
            .filter((value, index, self) => self.indexOf(value) === index)
            .sort((a, b) => a.localeCompare(b))

          // Sort by group name.
          this.suggestedSearchResults = this.suggestedSearchResults.sort(
            (a: SearchResult, b: SearchResult) =>
              a[this.groupBy]?.name.localeCompare(b[this.groupBy]?.name)
          )

          // Insert a group header before each group.
          let currentGroup: string = null
          this.suggestedSearchResults.forEach((result: SearchResult, index) => {
            if (result[this.groupBy]?.name !== currentGroup) {
              this.suggestedSearchResults.splice(index, 0, {
                id: null,
                shortLabel: result[this.groupBy]?.name,
                label: result[this.groupBy]?.name,
                resourceName: 'groupHeader'
              })
            }
            currentGroup = result[this.groupBy]?.name
          })
        }

        if (this.maxResults > 0) {
          this.suggestedSearchResults = this.suggestedSearchResults.slice(0, this.maxResults)
        }

        delete this.focusedItemIndex
      })
    }, 500)
  }

  // Transform an array of search results into an object of properties that have as value an array of ids.
  formatToEmit(
    selectedResults: SearchResult[]
  ): { [key: string]: any } | number {
    const emittedValueObject: {
      [key: string]: any
    } = this.resources.reduce((acc, resourceDefinition: ResourceDefinition) => {
      acc[resourceDefinition.className] = []
      return acc
    }, {})

    selectedResults.forEach((searchResult: SearchResult) => {
      emittedValueObject[searchResult.resourceName].push(searchResult.id)
    })

    // If one item only, we return value directly instead of array.
    if (this.maxSelectedItems === 1) {
      return selectedResults[0]?.id || null
    }

    // Format "array-of-ids" name based on resource name. Ex: cars => carIds.
    return Object.keys(emittedValueObject).reduce((acc, resourceName) => {
      acc[this.firstLetterInLowerCase(resourceName) + 'Ids'] =
        emittedValueObject[resourceName]
      return acc
    }, {})
  }

  // Use arrowKeys and enter to select suggested themes with keyboard.
  navigateSuggestedValues(key: string): void {
    if (key === 'ArrowDown') {
      if (typeof this.focusedItemIndex === 'undefined') {
        this.showList = true
        this.focusedItemIndex = 0
      } else if (
        this.focusedItemIndex <
        this.suggestedSearchResults.length - 1
      ) {
        this.focusedItemIndex++
      }
    } else if (key === 'ArrowUp') {
      if (!this.focusedItemIndex) {
        this.showList = false
        delete this.focusedItemIndex
      } else {
        this.focusedItemIndex--
      }
    } else if (
      key === 'Enter' &&
      typeof this.focusedItemIndex !== 'undefined' &&
      this.suggestedSearchResults[this.focusedItemIndex]
    ) {
      this.toggleItem(this.suggestedSearchResults[this.focusedItemIndex])
      delete this.focusedItemIndex
    }
  }

  getResourceIcon(resourceClassName: string): string {
    const resource: ResourceDefinition = this.resources.find(
      (r) => r.className === resourceClassName
    )

    return resource ? resource.icon : this.defaultIcon
  }

  private firstLetterInLowerCase(string: string): string {
    return string.charAt(0).toLowerCase() + string.slice(1)
  }

  submitCreate(): void {
    this.resourceService
      .store(this.resources[0].slug, this.createForm.value)
      .then((res: { id: number; name: string }) => {
        // Transform into search result
        this.toggleItem({
          id: res.id,
          shortLabel: res.name,
          label: res.name,
          resourceName: this.resources[0].className
        })
        this.showCreateModal = false
        this.createForm.reset()
      })
  }

  onCreateInputKeyUp(keyupEvent: KeyboardEvent): void {
    if (keyupEvent.key === 'Enter') {
      this.submitCreate()
    }
  }

  // Click outside closes list
  @HostListener('document:click', ['$event.target'])
  clickOut(eventTarget) {
    if (this.showList && !this.elementRef.nativeElement.contains(eventTarget)) {
      this.showList = false
    }
  }
}
